/**
 * Pricing Section
 * Monthly price, credits, overage rates, expiry period
 */

import { UseFormReturn } from 'react-hook-form';
import { PackageFormValues } from '@/src/schemas/admin/package-schema';
import { EXPIRY_PERIODS } from '@/src/constants/admin/packages';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PricingSectionProps {
  form: UseFormReturn<PackageFormValues>;
}

export default function PricingSection({ form }: PricingSectionProps) {
  return (
    <div className="grid gap-6">
      {/* Monthly Prices */}
      <div className="grid grid-cols-1 gap-6">
        <FormField
          control={form.control}
          name="monthly_price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ราคารายเดือน (บาท) *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                ราคาต่อเดือนในสกุลเงินบาท
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Hidden for now - USD support for future use */}
        {/* <FormField
          control={form.control}
          name="monthly_price_usd"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ราคารายเดือน (USD)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                ราคาต่อเดือนในสกุลเงินดอลลาร์ (ไม่บังคับ - สำหรับอนาคต)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        /> */}
      </div>

      {/* Credits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="included_credits"
          render={({ field }) => (
            <FormItem>
              <FormLabel>เครดิตที่รวมในแพ็กเกจ *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={-1}
                  placeholder="100"
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                จำนวนเครดิตต่อเดือน (-1 = ไม่จำกัด)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="max_credit_purchase"
          render={({ field }) => (
            <FormItem>
              <FormLabel>จำนวนเครดิตซื้อเพิ่มสูงสุด *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={-1}
                  placeholder="1000"
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                เครดิตเพิ่มเติมที่ซื้อได้สูงสุด (-1 = ไม่จำกัด)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Overage Rates */}
      <div className="grid grid-cols-1 gap-6">
        <FormField
          control={form.control}
          name="overage_rate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>อัตราเครดิตเพิ่ม (บาท/เครดิต) *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="0.90"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                ราคาต่อเครดิตที่ลูกค้าซื้อเพิ่ม (แนะนำ: 0.70-1.00 บาท)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Hidden for now - USD support for future use */}
        {/* <FormField
          control={form.control}
          name="overage_rate_usd"
          render={({ field }) => (
            <FormItem>
              <FormLabel>อัตราเครดิตเพิ่ม (USD/เครดิต)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  step={0.001}
                  placeholder="0.027"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                ราคาต่อเครดิตใน USD (ไม่บังคับ - สำหรับอนาคต)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        /> */}
      </div>

      {/* Credit Expiry */}
      <FormField
        control={form.control}
        name="credit_expiry_period"
        render={({ field }) => (
          <FormItem>
            <FormLabel>ระยะเวลาหมดอายุของเครดิต *</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="เลือกระยะเวลา" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {EXPIRY_PERIODS.map((period) => (
                  <SelectItem key={period} value={period}>
                    {period === 'never' ? 'ไม่มีกำหนด' : period}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>
              ระยะเวลาที่เครดิตจะหมดอายุนับจากวันที่ได้รับ
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
